import React, { useCallback, useEffect, useState } from "react";
import Loading from "../common/Loading";
import MusicShell from "../fanHome/MusicShell";
import { ACCENT } from "../artistStudio/shared";
import { parentalApi } from "./parentalApi";
import LimitsEditor from "./LimitsEditor";
import AllowanceEditor, { money } from "./AllowanceEditor";
import WeekSummary from "./WeekSummary";
import { AskPanel, IncomingRequests } from "./Requests";

// Parental Control 13+ (sheet 1.1.1 to 1.1.3): link a teen account (13 to 17) with a parent or guardian.
//   - An adult creates a one-time code and gives it to the teen.
//   - The teen enters it here and the accounts are linked.
//   - Only the parent can remove a link. The link ends by itself when the teen turns 18.
export { parentalApi };
const messageOf = (e, fallback = "Something went wrong. Please try again.") => e?.response?.data?.message || fallback;
const when = (value) => (value ? new Date(value).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "");

const Card = ({ title, children, testId }) => (
  <section className="mt-6 rounded-2xl border border-gray-200 bg-white p-5" data-testid={testId}>
    <h2 className="text-base font-semibold text-gray-900">{title}</h2>
    <div className="mt-3">{children}</div>
  </section>
);
const Primary = ({ children, ...props }) => (
  <button type="button" {...props} className="rounded-full px-5 py-2 text-sm font-semibold text-white disabled:opacity-60" style={{ backgroundColor: ACCENT }}>{children}</button>
);

function PersonRow({ person, note, action, below }) {
  return (
    <li className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 py-3 last:border-0">
      <div className="min-w-0">
        <p className="truncate font-semibold text-gray-900">{person?.name}</p>
        {person?.username && <p className="truncate text-sm text-gray-500">@{person.username}</p>}
        {note && <p className="text-xs text-gray-500">{note}</p>}
      </div>
      {action}
      {below && <div className="w-full">{below}</div>}
    </li>
  );
}

// Parental Control, sheet 1.4.4: what is different for an account under 18. The list comes from the server (data.restrictions) so this
// page always shows the rules the server really enforces. Shown to the teen and to the parent.
function Restrictions({ list }) {
  if (!list || !list.length) return null;
  return (
    <Card title="What is different for accounts under 18" testId="restrictions">
      <ul className="list-disc space-y-1 pl-5 text-sm text-gray-700">
        {list.map((r) => <li key={r.code}>{r.label}</li>)}
      </ul>
      <p className="mt-3 text-xs text-gray-500">These rules end by themselves when the account turns 18.</p>
    </Card>
  );
}

function TeenView({ data, reload }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setError(""); setDone("");
    if (!/^[A-Za-z0-9]{8}$/.test(code.trim())) return setError("The code is 8 letters and numbers.");
    setBusy(true);
    try {
      await parentalApi.link(code.trim());
      setCode(""); setDone("Linked. Your parent or guardian has been told.");
      await reload();
    } catch (err) {
      setError(messageOf(err));
    } finally { setBusy(false); }
  };
  const full = data.guardians.length >= data.limits.max_guardians;
  return (
    <>
      {data.needs_guardian && (
        <p className="mt-6 rounded-2xl bg-amber-50 px-5 py-4 text-amber-900" role="status" data-testid="needs-guardian">
          Your account is not linked to a parent or guardian yet. Ask them to create a code in their account, then enter it below.
        </p>
      )}
      {!full && (
        <Card title="Enter your parent's code" testId="teen-link-form">
          <form onSubmit={submit} className="flex flex-wrap items-start gap-3">
            <input
              value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} maxLength={8} autoComplete="off" spellCheck={false}
              aria-label="Parent code" placeholder="8-character code" disabled={busy}
              className="w-48 rounded-lg border border-gray-300 px-3 py-2 font-mono text-lg tracking-widest"
            />
            <Primary type="submit" disabled={busy}>{busy ? "Linking…" : "Link account"}</Primary>
          </form>
          {error && <p className="mt-2 text-sm text-red-600" role="alert">{error}</p>}
          {done && <p className="mt-2 text-sm text-green-700" role="status">{done}</p>}
        </Card>
      )}
      <Card title="Your parents or guardians" testId="guardian-list">
        {data.guardians.length === 0 ? <p className="text-sm text-gray-500">Nobody is linked yet.</p> : (
          <ul>{data.guardians.map((g) => <PersonRow key={g.link_id} person={g.person} note={`Linked ${when(g.linked_at)}`} />)}</ul>
        )}
        <p className="mt-3 text-xs text-gray-500">Only a parent or guardian can remove a link. It ends by itself when you turn 18.</p>
      </Card>
      {data.usage?.applies && (
        <Card title="Your time limits" testId="teen-limits">
          {data.usage.limit_seconds !== null && (
            <p className="text-sm text-gray-700">Today you have used {Math.floor(data.usage.used_seconds / 60)} of {Math.floor(data.usage.limit_seconds / 60)} minutes{data.usage.bonus_seconds > 0 ? ` (includes ${Math.floor(data.usage.bonus_seconds / 60)} extra minutes your parent approved)` : ""}.</p>
          )}
          {data.usage.window_start_text && (
            <p className="mt-1 text-sm text-gray-700">You can use the app between {data.usage.window_start_text} and {data.usage.window_end_text}.</p>
          )}
          <p className="mt-3 text-xs text-gray-500">Your parent or guardian sets these limits.</p>
        </Card>
      )}
      {data.allowance?.applies && (
        <Card title="Your spending allowance" testId="teen-allowance">
          {data.allowance.monthly_amount === 0 ? (
            <p className="text-sm text-gray-700">Your parent or guardian has not turned on spending for your account.</p>
          ) : (
            <p className="text-sm text-gray-700">You have {money(data.allowance.remaining)} left of your {money(data.allowance.allowance_this_month ?? data.allowance.monthly_amount)} this month{data.allowance.extra_amount > 0 ? ` (includes ${money(data.allowance.extra_amount)} extra your parent approved)` : ""}.</p>
          )}
          <p className="mt-3 text-xs text-gray-500">A payment that is more than what is left is stopped. Ask your parent or guardian if you need more.</p>
        </Card>
      )}
      {data.guardians.length > 0 && <AskPanel canTime={Boolean(data.usage?.applies && data.usage.limit_seconds !== null)} canMoney={Boolean(data.allowance?.applies)} />}
      <Restrictions list={data.restrictions} />
    </>
  );
}

function ParentView({ data, reload }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [fresh, setFresh] = useState(null);
  const [copied, setCopied] = useState(false);
  const [confirming, setConfirming] = useState(null);
  const [limitsFor, setLimitsFor] = useState(null);

  const create = async () => {
    setBusy(true); setError(""); setCopied(false);
    try { setFresh(await parentalApi.createCode()); await reload(); } catch (e) { setError(messageOf(e)); } finally { setBusy(false); }
  };
  const remove = async (id) => {
    setBusy(true); setError("");
    try { await parentalApi.remove(id); setConfirming(null); if (fresh?.link_id === id) setFresh(null); await reload(); } catch (e) { setError(messageOf(e)); } finally { setBusy(false); }
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(fresh.code); setCopied(true); } catch (e) { setCopied(false); }
  };

  return (
    <>
      <Card title="Link a teen account" testId="parent-code">
        <p className="text-sm text-gray-600">Create a one-time code and give it to your teen (13 to 17). They enter it on this page in their own account. A code works once and expires in {data.limits.code_valid_days} days.</p>
        <div className="mt-3"><Primary onClick={create} disabled={busy}>{busy ? "Please wait…" : "Create a code"}</Primary></div>
        {error && <p className="mt-2 text-sm text-red-600" role="alert">{error}</p>}
        {fresh && (
          <div className="mt-4 rounded-xl bg-green-50 px-4 py-4" data-testid="fresh-code">
            <p className="text-xs uppercase tracking-wide text-gray-500">Your code (shown once)</p>
            <p className="mt-1 font-mono text-2xl font-bold tracking-widest text-gray-900">{fresh.code}</p>
            <p className="mt-1 text-xs text-gray-600">Expires {when(fresh.expires_at)}</p>
            <button type="button" onClick={copy} className="mt-2 text-sm font-semibold underline" style={{ color: ACCENT }}>{copied ? "Copied" : "Copy code"}</button>
          </div>
        )}
      </Card>

      <Card title="Linked teens" testId="children-list">
        {data.children.length === 0 ? <p className="text-sm text-gray-500">No teen accounts are linked to you yet.</p> : (
          <ul>
            {data.children.map((c) => (
              <PersonRow
                key={c.link_id} person={c.person} note={`Linked ${when(c.linked_at)}`}
                below={limitsFor === c.person?.id ? <><LimitsEditor childId={c.person.id} /><AllowanceEditor childId={c.person.id} /></> : null}
                action={<span className="flex items-center gap-4"><button type="button" onClick={() => setLimitsFor(limitsFor === c.person?.id ? null : c.person?.id)} className="text-sm font-semibold underline" style={{ color: ACCENT }} aria-expanded={limitsFor === c.person?.id} aria-label={`Limits and allowance for ${c.person?.name}`}>Limits &amp; allowance</button>{confirming === c.link_id ? (
                  <span className="flex items-center gap-3 text-sm">
                    <button type="button" onClick={() => remove(c.link_id)} disabled={busy} className="font-semibold text-red-600 underline" aria-label={`Confirm remove ${c.person?.name}`}>Remove link</button>
                    <button type="button" onClick={() => setConfirming(null)} className="text-gray-500 underline">Keep</button>
                  </span>
                ) : <button type="button" onClick={() => setConfirming(c.link_id)} className="text-sm font-semibold text-red-600 underline" aria-label={`Remove ${c.person?.name}`}>Remove</button>}</span>}
              />
            ))}
          </ul>
        )}
        <p className="mt-3 text-xs text-gray-500">A link ends by itself the day your teen turns 18.</p>
      </Card>

      {data.children.length > 0 && <IncomingRequests />}

      {data.children.length > 0 && <WeekSummary />}

      <Restrictions list={data.restrictions} />

      {data.open_codes.length > 0 && (
        <Card title="Unused codes" testId="open-codes">
          <ul>
            {data.open_codes.map((c) => (
              <li key={c.link_id} className="flex items-center justify-between border-b border-gray-100 py-3 last:border-0">
                <span><span className="font-mono tracking-widest">{c.code_hint}</span> <span className="text-xs text-gray-500">expires {when(c.expires_at)}</span></span>
                <button type="button" onClick={() => remove(c.link_id)} disabled={busy} className="text-sm font-semibold text-red-600 underline" aria-label={`Cancel code ${c.code_hint}`}>Cancel</button>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </>
  );
}

export default function FamilyLink() {
  const [state, setState] = useState({ status: "loading", data: null, error: "" });
  const load = useCallback(async () => {
    try { setState({ status: "ready", data: await parentalApi.me(), error: "" }); }
    catch (e) { setState((s) => ({ status: s.data ? "ready" : "error", data: s.data, error: messageOf(e, "We couldn't load your family settings.") })); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const { status, data, error } = state;
  return (
    <MusicShell>
      <h1 className="text-2xl font-bold text-gray-900">Family</h1>
      <p className="mt-1 text-sm text-gray-500">Link a teen account (13 to 17) with a parent or guardian, and set their time limits and spending allowance.</p>
      {status === "loading" && <Loading />}
      {status === "error" && (
        <p className="mt-6 rounded-2xl border border-gray-200 bg-gray-50 px-5 py-6 text-center text-gray-700" role="alert">
          {error} <button type="button" onClick={() => { setState({ status: "loading", data: null, error: "" }); load(); }} className="font-semibold underline" style={{ color: ACCENT }}>Try again</button>
        </p>
      )}
      {status === "ready" && data.kind === "youth" && <TeenView data={data} reload={load} />}
      {status === "ready" && data.kind === "adult" && <ParentView data={data} reload={load} />}
      {status === "ready" && (data.kind === "unknown" || data.kind === "underage") && (
        <p className="mt-6 rounded-2xl bg-gray-50 px-5 py-4 text-gray-700">Family linking is available on personal accounts of people aged 13 and over.</p>
      )}
    </MusicShell>
  );
}
